import ArrowUp from '@assets/images/icons/arrow-up.svg?react'
import { ModalHeader } from '@components/App/Modal/components/ModalHeader'
import * as ModalTypes from '@components/App/Modal/constants'
import { MODAL_BLOCK_CONTENT_VIEW } from '@components/App/Modal/constants'
import { Node } from '@components/App/Modal/PageContentView/components/Node'
import { Comments } from '@components/ArtBoard/Comments'
import { getCommentsByCommentableAndType } from '@components/ArtBoard/CommentsLegacy/selectors'
import { type ID, selectBlockParent } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { CommentsActions } from '@store/reducers/comments'
import type { ProjectManager } from '@store/reducers/manager/types'
import { loginStrategy } from '@storeOld/reducers/shared'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DEFAULT_PAGE_NAME } from '@/constants/defaultNames'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useAuth } from '@/hooks'
import type { AppState } from '@/types'
import { buildRecipient } from '@/utils'
import css from './BlockModal.module.css'
import { BlockEditor } from './components'

const useCommentsStore = (id: string, manager: ProjectManager) => {
  const comments = getCommentsByCommentableAndType(manager.comments, id)

  const dispatch = useDispatch()

  const onCommit = (payload: any) => {
    dispatch(
      CommentsActions.create({
        ...payload,
        commentable: id,
        commentableType: 2,
      })
    )

    return Promise.resolve()
  }

  const onLogin = () =>
    // @ts-expect-error
    dispatch(loginStrategy({ type: ModalTypes.MODAL_BLOCK }))
  const onUpdate = (payload: any) => dispatch(CommentsActions.update(payload))

  return { onLogin, onUpdate, onCommit, comments }
}

type Props = {
  id: ID
  onClose: () => void
  projectOwnerPlan: string | null
  focusOnComment?: boolean
}

export const BlockModal: React.FC<Props> = ({
  id,
  onClose,
  focusOnComment,
}) => {
  const activeTabId = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.tabs.activeTab
  )

  const nodeId = useSelector(selectBlockParent(id))[0]
  const { title: nodeTitle, colorId: nodeColorId } = useSelector(
    migrationProjectSelector.getProjectNodeById(nodeId)
  )

  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const canEdit = useSelector(migrationProjectSelector.getProjectAccessCanEdit)
  const canEditContent = useSelector(
    migrationProjectSelector.getProjectAccessCanEditContent
  )
  const canComment = useSelector(
    migrationProjectSelector.getProjectAccessCanComment
  )

  const canWriteBlockContent = canEdit || canEditContent

  const manager = useSelector<AppState, ProjectManager>(
    (state) => state.manager
  )

  const nodeColorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(nodeColorId)
  )
  const node = useSelector(migrationProjectSelector.getProjectNodeById(nodeId))
  const { title, colorId } = node
  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const auth = useAuth()
  const author = buildRecipient(auth)

  const loggedIn = !!auth.id

  const dispatch = useDispatch()

  const { onLogin, comments, onUpdate, onCommit } = useCommentsStore(
    id,
    manager
  )

  const handleContentOpen = () => {
    // @ts-expect-error
    dispatch(openModal(MODAL_BLOCK_CONTENT_VIEW, { nodeId, scrolless: true }))
  }

  function onAddClick(payload: any) {
    sendGtmEvent(GtmTriggerName.BLOCK_COMMENT_ADD, 'Submit comment for block')

    return onCommit(payload)
  }

  return (
    <div className={css.wrapper}>
      <ModalHeader
        color={nodeColorHex}
        icon={<ArrowUp style={{ transform: 'rotate(-90deg)' }} />}
        title={nodeTitle || DEFAULT_PAGE_NAME}
        copyUrl={`${window.location.origin}/${projectId}/${activeTabId}/b/${id}/note`}
        withSyncStatus
        onTitleClick={handleContentOpen}
        onClose={onClose}
      />

      <div className={css.block_wrapper}>
        <div className={css.content}>
          <BlockEditor readOnly={!canWriteBlockContent} blockId={id} />

          {canComment && (
            <div className={css.comments}>
              <Comments
                comments={comments}
                loggedIn={loggedIn}
                author={author}
                onCommit={onAddClick}
                onLogin={onLogin}
                localStorageKey={`${id}_block`}
                onUpdate={onUpdate}
                focus={focusOnComment}
              />
            </div>
          )}
        </div>
        <div className={css.sidebar}>
          <Node name={title} color={colorHex} blockId={id} {...node} />
        </div>
      </div>
    </div>
  )
}
